import { useEffect, useMemo, useState } from "react";
import {
  AppointmentDto,
  useScholarAppointmentsQuery,
  useTeacherAppointmentsQuery,
} from "../../api/appointmentApi.ts";
import { useLanguageStore } from "../../stores/languageStore.ts";
import {
  getTranslations,
  useSupportedInstruments,
} from "../../api/instrumentApi.ts";
import { CalendarEvent } from "../calendar.ts";
import Calendar from "../Calendar.tsx";
import { useResponsiveCalendar } from "../../hooks/useResponsiveCalendar.ts";
import AppointmentDetailsModal from "./AppointmentDetailsModal.tsx";
import { Trans } from "@lingui/react/macro";
import {
  Calendar as CalendarIcon,
  GraduationCap,
  Music,
  Repeat,
} from "lucide-react";

type AppointmentsInfiniteQuery = ReturnType<typeof useScholarAppointmentsQuery>;

interface DashboardCalendarViewProps {
  scholarQuery: AppointmentsInfiniteQuery;
  teacherQuery: ReturnType<typeof useTeacherAppointmentsQuery>;
  isTeacher: boolean;
}

/** Eagerly loads every remaining page so the calendar can show all weeks. */
function useLoadAllPages(query: AppointmentsInfiniteQuery) {
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage().catch((e) => console.error(e));
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
}

export default function DashboardCalendarView({
  scholarQuery,
  teacherQuery,
  isTeacher,
}: DashboardCalendarViewProps) {
  const { language, dateFnsLocale } = useLanguageStore();
  const { data: supportedInstruments } = useSupportedInstruments();
  const { calendarViewType } = useResponsiveCalendar();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selected, setSelected] = useState<AppointmentDto | null>(null);

  useLoadAllPages(scholarQuery);
  useLoadAllPages(teacherQuery);

  const events = useMemo<CalendarEvent[]>(() => {
    const toEvent = (
      appointment: AppointmentDto,
      kind: "teaching" | "attending",
    ): CalendarEvent => {
      const instrument = getTranslations(
        supportedInstruments,
        appointment.instrument,
        language,
      );
      return {
        from: new Date(appointment.from),
        to: new Date(appointment.to),
        title: `${instrument} · ${appointment.personName}`,
        color: kind === "teaching" ? "secondary" : "primary",
        icon: appointment.source === "SUBSCRIPTION" ? Repeat : CalendarIcon,
        cornerIcon: kind === "teaching" ? GraduationCap : Music,
        data: appointment,
      };
    };

    const scholarAppointments =
      scholarQuery.data?.pages.flatMap((p) => p.appointments) ?? [];
    const teacherAppointments = isTeacher
      ? (teacherQuery.data?.pages.flatMap((p) => p.appointments) ?? [])
      : [];

    return [
      ...teacherAppointments.map((a) => toEvent(a, "teaching")),
      ...scholarAppointments.map((a) => toEvent(a, "attending")),
    ];
  }, [
    scholarQuery.data,
    teacherQuery.data,
    isTeacher,
    supportedInstruments,
    language,
  ]);

  return (
    <div className="flex flex-col gap-3">
      {/* Legend */}
      <div className="text-soluud-text-secondary flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        {isTeacher && (
          <span className="flex items-center gap-1.5">
            <GraduationCap className="text-soluud-secondary h-3.5 w-3.5" />
            <Trans>Teaching</Trans>
          </span>
        )}
        <span className="flex items-center gap-1.5">
          <Music className="text-soluud-primary h-3.5 w-3.5" />
          <Trans>Attending</Trans>
        </span>
        <span className="bg-soluud-border h-3 w-px" />
        <span className="flex items-center gap-1.5">
          <Repeat className="h-3 w-3" />
          <Trans>Subscription</Trans>
        </span>
        <span className="flex items-center gap-1.5">
          <CalendarIcon className="h-3 w-3" />
          <Trans>Single</Trans>
        </span>
      </div>

      <Calendar
        events={events}
        currentDate={currentDate}
        onDateChange={setCurrentDate}
        viewType={calendarViewType}
        locale={dateFnsLocale}
        hourRowHeightRem={5}
        emptyLabel={<Trans>No lessons scheduled</Trans>}
        onEventClick={(event) => setSelected(event.data as AppointmentDto)}
      />

      <AppointmentDetailsModal
        appointment={selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}
